import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Users, Pencil, Trash2, LogIn, Bot, Loader2, Search, Shield, User } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";

const readToken = () => {
  try { const v = sessionStorage.getItem("___at"); return v ? atob(v) : null; } catch { return null; }
};
const storeToken = (token) => {
  try { sessionStorage.setItem("___at", btoa(token)); } catch {}
};

export default function UserManagement() {
  const { user, checkAuth } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editLimit, setEditLimit] = useState("1");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const token = readToken();
      const { data } = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleUpdateLimit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const token = readToken();
      const maxChatbots = editLimit === "unlimited" ? -1 : parseInt(editLimit, 10);
      await axios.patch(`${API}/admin/users/${editUser.id}`, { max_chatbots: maxChatbots }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Updated chatbot limit for ${editUser.name}`);
      setEditUser(null);
      fetchUsers();
    } catch (e) {
      toast.error("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteConfirmName !== deleteTarget.name) return;
    setSaving(true);
    try {
      const token = readToken();
      await axios.delete(`${API}/admin/users/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted user ${deleteTarget.email}`);
      setDeleteTarget(null);
      setDeleteConfirmName("");
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    } finally {
      setSaving(false);
    }
  };

  const handleImpersonate = async (targetUser) => {
    try {
      const token = readToken();
      // Save admin token for returning later
      sessionStorage.setItem("___admin_token", token);
      const { data } = await axios.post(`${API}/admin/impersonate/${targetUser.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      storeToken(data.token);
      await checkAuth();
      toast.success(`Now managing as ${targetUser.name}`);
    } catch (e) {
      toast.error("Failed to switch to user account");
    }
  };

  const formatLimit = (val) => {
    if (val === -1) return "Unlimited";
    return String(val);
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <PageHeader title="User Management" subtitle="Invite and manage workspace members and their permissions." />
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-none"
          data-testid="user-search-input"
        />
      </div>

      {/* User Table */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left p-3 font-medium">User</th>
              <th className="text-left p-3 font-medium">Role</th>
              <th className="text-left p-3 font-medium">Chatbots</th>
              <th className="text-left p-3 font-medium">Limit</th>
              <th className="text-left p-3 font-medium">Joined</th>
              <th className="text-right p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {u.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 ${u.is_platform_admin ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {u.is_platform_admin ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                    {u.is_platform_admin ? "Platform admin" : "User"}
                  </span>
                </td>
                <td className="p-3">
                  <span className="flex items-center gap-1">
                    <Bot className="w-3 h-3 text-muted-foreground" />
                    {u.chatbot_count || 0}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-xs px-2 py-1 bg-muted font-mono">
                    {formatLimit(u.max_chatbots ?? 1)}
                  </span>
                </td>
                <td className="p-3 text-muted-foreground text-xs">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditUser(u);
                        setEditLimit(u.max_chatbots === -1 ? "unlimited" : String(u.max_chatbots ?? 1));
                      }}
                      title="Edit chatbot limit"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {!u.is_platform_admin && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleImpersonate(u)}
                          title="Login as this user"
                          data-testid={`impersonate-user-${u.id}`}
                        >
                          <LogIn className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-[hsl(var(--signal-red))] hover:text-[hsl(var(--signal-red))]"
                          onClick={() => { setDeleteTarget(u); setDeleteConfirmName(""); }}
                          title="Delete user"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-muted-foreground">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Limit Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit Chatbot Limit</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3 p-3 bg-muted">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold">
                  {editUser.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{editUser.name}</p>
                  <p className="text-xs text-muted-foreground">{editUser.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="overline">Max Chatbots</Label>
                <Select value={editLimit} onValueChange={setEditLimit}>
                  <SelectTrigger className="rounded-none" data-testid="chatbot-limit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Currently using {editUser.chatbot_count || 0} chatbot(s)
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={handleUpdateLimit} disabled={saving} data-testid="save-limit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[hsl(var(--signal-red))]">Delete User Account</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                This will permanently delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) and all their chatbots, conversations, and data.
              </span>
              <span className="block font-medium text-foreground">
                Type the user's name to confirm:
              </span>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={deleteTarget?.name}
                className="rounded-none"
                data-testid="delete-confirm-input"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" onClick={() => { setDeleteTarget(null); setDeleteConfirmName(""); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteConfirmName !== deleteTarget?.name || saving}
              className="bg-[hsl(var(--signal-red))] hover:bg-[hsl(var(--signal-red))]/90 rounded-none disabled:opacity-50"
              data-testid="confirm-delete-user-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
